import { verifyLogin } from "util/cache"
import { useGetClassQuery, useGetStudentLessonResultQuery } from "util/rtk-api"
import { jsPDF } from 'jspdf'
import { exportTableToXlsx, svgUrlToPngDataUrl } from "util/export"

import { SECTIONS, normalizeSection } from "@all-for-one/shared-types"
import Breadcrumbs from "components/breadcrumb"
import { QuestionRow } from "components/question-row"
import { DownloadButton } from "components/blue-button"
import { DownloadActionDropdown } from "components/action-dropdown"
import styles from 'css/pages/student-lesson-results.module.css'
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useNavigate, useParams } from "react-router-dom"
import { StudentLessonResultContextProvider } from "contexts/student-lesson-result-context"
import firecrackerLogo from "img/logo_firecracker-cropped.svg"

const sectionLabels: Record<SECTIONS, string> = {
  pre_assessment: 'Pre-Assessment',
  post_assessment: 'Post-Assessment',
  check_for_understanding: 'Stop & Check',
  independent_work: 'Independent Work'
}
const sectionOrder: SECTIONS[] = [
  SECTIONS.PRE_ASSESSMENT,
  SECTIONS.CHECK_FOR_UNDERSTANDING,
  SECTIONS.INDEPENDENT_WORK,
  SECTIONS.POST_ASSESSMENT
]

export const StudentLessonResultsPage = () => {
  const { classId, studentId, lessonId } = useParams()
  const navigate = useNavigate()
  const classQuery = useGetClassQuery({ classId: classId! })
  const classDetails = classQuery.data
  const { data: results } = useGetStudentLessonResultQuery({ classId: classId!, studentId: studentId!, lessonId: lessonId! })
  const [downloadDropdownPos, setDownloadDropdownPos] = useState<{ top: number; left: number } | null>(null)
  const downloadBtnRef = useRef<HTMLDivElement | null>(null)
  const downloadPortalRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    verifyLogin(navigate)
  })

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node
      if (downloadDropdownPos &&
        downloadBtnRef.current && !downloadBtnRef.current.contains(target) &&
        downloadPortalRef.current && !downloadPortalRef.current.contains(target)) {
        setDownloadDropdownPos(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [downloadDropdownPos])

  const studentEntry = classDetails?.students?.find(s => s.studentId === studentId)

  const grouped = (results?.results ?? []).reduce((acc, r) => {
    const key = r.section as SECTIONS
    if (!key) return acc
      ; (acc[key] ||= []).push(r)
    return acc
  }, {} as Record<SECTIONS, typeof results.results>)

  const handleDownloadClick = () => {
    if (downloadDropdownPos) {
      setDownloadDropdownPos(null)
    } else if (downloadBtnRef.current) {
      const rect = downloadBtnRef.current.getBoundingClientRect()
      setDownloadDropdownPos({ top: rect.bottom + 4, left: rect.left })
    }
  }

  const buildDownloadRows = () => {
    const headers = ['Section', 'Question', 'Student Answer', 'Expected Answer', 'Score %']
    const rows: (string | number)[][] = []
    for (const s of sectionOrder) {
      const qs = grouped[s] ?? []
      for (const r of qs) {
        const effectivePct = Math.round(((r.manualScore ?? r.score ?? (r.correct ? 1 : 0)) * 100))
        rows.push([
          sectionLabels[s],
          r.questionText ?? '',
          r.studentAnswer ?? '',
          r.correctAnswer ?? '',
          effectivePct,
        ])
      }
    }
    return { headers, rows }
  }

  const handleDownloadXLS = () => {
    const { headers, rows } = buildDownloadRows()
    const date = new Date().toLocaleDateString()
    const overallScore = results?.overallScore != null ? Number(results.overallScore).toFixed(1) + '%' : '-'
    exportTableToXlsx({
      fileName: `${results?.studentName ?? 'student'}_${results?.lessonName ?? 'lesson'}.xlsx`,
      sheetName: 'Questions',
      headers,
      rows,
      branding: { titleLines: [
        `Class Name: ${classDetails?.name ?? ''}`,
        `Lesson Name: ${results?.lessonName ?? ''}`,
        `Student Name: ${results?.studentName ?? ''}`,
        `UserID: ${studentId ?? ''}`,
        `Download Date: ${date}`,
        `Overall Score: ${overallScore}`,
      ] }
    })
  }

  const handleDownloadPDF = async () => {
    const date = new Date().toLocaleDateString()
    const overallScore = results?.overallScore != null ? Number(results.overallScore).toFixed(1) + '%' : '-'
    const logoDataUrl = await svgUrlToPngDataUrl(firecrackerLogo)

    const doc = new jsPDF()
    let y = 14

    // Logo
    if (logoDataUrl) {
      doc.addImage(logoDataUrl, 'PNG', 14, y, 13, 13)
      y += 18
    }

    // Summary header
    doc.setFontSize(10)
    doc.setTextColor(100)
    const summaryLine = `Class Name: ${classDetails?.name ?? ''} | Lesson Name: ${results?.lessonName ?? ''} | Student Name: ${results?.studentName ?? ''} | UserID: ${studentId ?? ''}`
    doc.text(summaryLine, 14, y)
    y += 6
    doc.text(`Download Date: ${date} | Overall Score: ${overallScore}`, 14, y)
    y += 10

    // Separator line
    doc.setDrawColor(180)
    doc.line(14, y, 196, y)
    y += 8

    // Sections with questions in document format
    for (const s of sectionOrder) {
      const qs = grouped[s] ?? []
      if (!qs.length) continue

      // Section score
      const scoreData = results?.sectionScores?.find(ss =>
        ss.section === sectionLabels[s] || normalizeSection(ss.section) === s
      )
      const sectionScore = scoreData?.score != null ? Number(scoreData.score).toFixed(1) + '%' : '-'

      // Section header
      doc.setFontSize(12)
      doc.setTextColor(41, 128, 185)
      doc.setFont('helvetica', 'bold')
      doc.text(`${sectionLabels[s]}: ${sectionScore}`, 14, y)
      y += 7

      for (let i = 0; i < qs.length; i++) {
        const r = qs[i]
        const effectivePct = Math.round((r.manualScore ?? r.score ?? (r.correct ? 1 : 0)) * 100)

        // Check if we need a new page (leave room for a full question block)
        if (y > 260) {
          doc.addPage()
          y = 14
        }

        // Question number + score
        doc.setFontSize(10)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(0)
        doc.text(`${i + 1}. ${effectivePct}%`, 14, y)
        y += 5

        // Question text (wrapped)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(40)
        const questionLines = doc.splitTextToSize(r.questionText ?? '', 170)
        doc.text(questionLines, 18, y)
        y += questionLines.length * 5

        // Student Answer
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(0)
        doc.text('Student Answer: ', 18, y)
        const saX = 18 + doc.getTextWidth('Student Answer: ')
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(effectivePct >= 70 ? 34 : 180, effectivePct >= 70 ? 139 : 40, effectivePct >= 70 ? 34 : 40)
        const answerLines = doc.splitTextToSize(r.studentAnswer ?? '-', 170 - doc.getTextWidth('Student Answer: '))
        doc.text(answerLines, saX, y)
        y += answerLines.length * 5

        // Expected Answer
        doc.setFont('helvetica', 'italic')
        doc.setTextColor(120)
        const expectedLines = doc.splitTextToSize(`Expected: ${r.correctAnswer ?? '-'}`, 170)
        doc.text(expectedLines, 18, y)
        y += expectedLines.length * 5 + 4
      }

      y += 4
    }

    doc.save(`${results?.studentName ?? 'student'}_${results?.lessonName ?? 'lesson'}.pdf`)
  }

  return (
    <StudentLessonResultContextProvider studentId={studentId} lessonId={lessonId} classId={classId}>
      <div className={styles.studentLessonResultsPageContainer}>
        <Breadcrumbs items={[
          { label: 'My Classrooms', path: '/classrooms' },
          { label: classDetails?.name, path: `/classrooms/${results?.classId}` },
          { label: results?.studentName, path: `/classrooms/${results?.classId}/students/${studentId}` },
          { label: results?.lessonName }
        ]} />

        <div className={styles.row}>
          <span className={styles.lessonName}>{results?.lessonName}</span>
        </div>
        <div className={styles.row}>
          <span className={styles.bold}>{results?.studentName.toUpperCase()}</span>
          {results?.difficulty && (
            <span className={styles.difficultyText}>Level when taken: {results.difficulty}</span>
          )}
          <div className={styles.overallScore}>
            OVERALL: {results?.overallScore != null ? Number(results.overallScore).toFixed(1) : '-'}%
          </div>
          <div ref={downloadBtnRef}>
            <DownloadButton onClick={handleDownloadClick} />
          </div>
        </div>
        <div className={styles.resultsContainer}>
          {sectionOrder.filter(s => grouped[s]?.length).map(s => (
            <div key={s}>
              <div className={styles.sectionHeader}>
                {sectionLabels[s]}: {
                  (() => {
                    // Normalize section name for comparison (EduQuery uses 'Pre-Assessment', code uses 'pre_assessment')
                    const sectionName = sectionLabels[s]; // e.g., 'Pre-Assessment'
                    const scoreData = results?.sectionScores?.find(ss =>
                      ss.section === sectionName ||
                      normalizeSection(ss.section) === s
                    );
                    return scoreData?.score != null ? Number(scoreData.score).toFixed(1) : '-';
                  })()
                }%
              </div>
              {grouped[s].map((r, i) => (
                <QuestionRow
                  key={r.questionId}
                  questionId={r.questionId}
                  index={i}
                  question={r.questionText}
                  answer={r.studentAnswer}
                  correct={r.correct}
                  score={r.score}
                  manualScore={r.manualScore}
                  manuallyGraded={r.manuallyGraded}
                  correctAnswer={r.correctAnswer}
                  questionType={r.questionType}
                  displayLabel={r.displayLabel}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
      {downloadDropdownPos && createPortal(
        <div ref={downloadPortalRef} style={{ position: 'fixed', top: downloadDropdownPos.top, left: downloadDropdownPos.left, zIndex: 9999 }}>
          <DownloadActionDropdown onDownloadXLS={handleDownloadXLS} onDownloadPDF={handleDownloadPDF} />
        </div>,
        document.body
      )}
    </StudentLessonResultContextProvider>
  )
}

